<template>
  <Loading v-if="loading" :title="t('loading')" fullscreen />
  <ErrorMessage v-else-if="error" :error-title="errorInfo?.title" :error-description="errorInfo?.description" />
  <div v-else-if="reference" class="v1-reference" data-testid="v1-reference">
    <!-- Code-bearing screens (reference / qr / virtual_account): hero code card →
         blue info note → details card → numbered instructions → optional map. No
         body <h1> and no CTA — the native chrome owns the header and there is
         nothing to submit (the reference is already issued). -->
    <template v-if="isCodeType">
      <V1ReferenceCodeCard
        v-for="(code, index) in codes"
        :key="index"
        :code="code"
        :data-testid="`v1-reference-code-${index}`"
      />

      <template v-for="(note, noteIndex) in notes" :key="noteIndex">
        <AlertBanner v-if="note.level === 'warn'" :content="note.body" icon-alt="Warning" />
        <Notice v-else :title="note.title ?? undefined" :body="note.body" />
      </template>

      <DetailList variant="rows" :items="detailItems" data-testid="v1-reference-details" />

      <div v-if="steps.length" class="v1-reference__instructions" data-testid="v1-reference-instructions">
        <DetailList
          variant="numbered"
          :title="t('instructions')"
          :items="stepItems"
          data-testid="v1-reference-steps"
        />
        <img
          v-if="instructionsImage"
          :src="instructionsImage"
          class="v1-reference__instructions-image"
          :alt="reference.provider.displayName"
        />
      </div>

      <div v-if="mapUrl" class="v1-reference__map" data-testid="v1-reference-map">
        <div class="v1-reference__map-frame">
          <span class="v1-reference__map-pill" data-testid="v1-reference-map-pill">
            <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
              <path
                fill="currentColor"
                d="M12 2a7 7 0 0 0-7 7c0 5 7 13 7 13s7-8 7-13a7 7 0 0 0-7-7Zm0 9.5A2.5 2.5 0 1 1 12 6.5a2.5 2.5 0 0 1 0 5Z"
              />
            </svg>
            {{ t("nearby-stores") }}
          </span>
          <iframe allow="geolocation 'src';" :src="mapUrl" loading="lazy" width="600" height="450" style="border: 0" />
        </div>
      </div>
    </template>

    <!-- Redirect: the reference is a hosted PSP page — a single CTA sends the
         browser there. -->
    <template v-else-if="isRedirect">
      <DetailList variant="rows" :items="detailItems" data-testid="v1-reference-details" />
      <div class="v1-reference__cta">
        <button class="primary" data-testid="v1-reference-primary" @click="onPrimary">{{ t("continue") }}</button>
      </div>
    </template>

    <!-- Forward-compat fallback (redesign §4): a type this build doesn't know
         still shows the summary + an "update the app" note and a way home. -->
    <template v-else>
      <DetailList variant="rows" :items="detailItems" data-testid="v1-reference-details" />
      <div data-testid="v1-reference-unsupported">
        <AlertBanner :content="t('payment-method-unsupported')" icon-alt="Warning" />
      </div>
      <div class="v1-reference__cta">
        <button class="primary" data-testid="v1-reference-primary" @click="onPrimary">{{ t("return-to-home") }}</button>
      </div>
    </template>
  </div>

  <NoPayments v-else />
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { AxiosError } from "axios";
import ErrorMessage from "@/components/ErrorMessage.vue";
import NoPayments from "@/components/NoPayments.vue";
import AlertBanner from "@/components/AlertBanner.vue";
import V1ReferenceCodeCard from "@/components/v1/V1ReferenceCodeCard.vue";
import { fetchV1PaymentInfo, fetchV1PaymentReference } from "@/helpers/v1TestHelper";
import {
  IV1PaymentReferenceResponse,
  IV1ReferenceCode,
  IV1ReferenceNote,
  V1PaymentReferenceType,
} from "@/types/IV1PaymentReference";
import { DetailList, Loading, Notice, type DetailListItem } from "@payjoy/design-system";

// The three code-bearing variants; `virtual_account` and `qr` reuse the same
// hero-card + instruction rendering as `reference`, differing only by `code.format`.
const CODE_TYPES: readonly string[] = [
  V1PaymentReferenceType.Reference,
  V1PaymentReferenceType.Qr,
  V1PaymentReferenceType.VirtualAccount,
];

/**
 * v1 payment-reference screen (PYMT-2288, EP-3) — renders `POST /api/v1/payment-reference/`
 * from its `type` alone: `reference`/`qr`/`virtual_account` share the code-card layout
 * (driven by `code.format`), `redirect` shows a CTA, and an unknown `type` degrades to
 * the summary + "update the app" note instead of crashing (redesign §3).
 *
 * Reached from the providers list / group drill-in; selection + `amount` + `days` arrive
 * as query params. `fetchV1PaymentInfo` runs first so the session-scoped call has a session.
 */
export default defineComponent({
  name: "V1PaymentReferenceView",
  components: {
    ErrorMessage,
    Loading,
    NoPayments,
    AlertBanner,
    DetailList,
    Notice,
    V1ReferenceCodeCard,
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const { t } = useI18n();
    const reference = ref<IV1PaymentReferenceResponse | null>(null);
    const loading = ref<boolean>(true);
    const errorInfo = ref<{ title?: string; description?: string } | null>(null);
    const error = computed<boolean>(() => errorInfo.value !== null);

    const queryString = (key: string): string =>
      typeof route.query[key] === "string" ? (route.query[key] as string) : "";

    onMounted(async () => {
      const providerId = queryString("providerId");
      const sectionId = queryString("sectionId");
      const amount = queryString("amount");
      const daysString = queryString("days");
      const days = Number(daysString);

      // A reference cannot be generated without the selected provider, amount
      // and a numeric term — a direct/stale navigation lands on NoPayments.
      // (`days` may legitimately be 0, so an empty string, not falsiness, is the
      // "missing" test.)
      if (!providerId || !amount || daysString === "" || !Number.isFinite(days)) {
        loading.value = false;
        return;
      }

      try {
        await fetchV1PaymentInfo();
        reference.value = await fetchV1PaymentReference({ providerId, sectionId, amount, days });
      } catch (fetchError) {
        console.error("[v1] Failed to generate payment reference:", fetchError);
        const problemBody = (fetchError as AxiosError<{ title?: string; detail?: string }>)?.response?.data;
        errorInfo.value = { title: problemBody?.title, description: problemBody?.detail };
      } finally {
        loading.value = false;
      }
    });

    const isRedirect = computed<boolean>(() => reference.value?.type === V1PaymentReferenceType.Redirect);
    const isCodeType = computed<boolean>(() => !!reference.value && CODE_TYPES.includes(reference.value.type));

    const codes = computed<IV1ReferenceCode[]>(() => {
      const r = reference.value;
      return r && CODE_TYPES.includes(r.type) && "codes" in r ? r.codes : [];
    });

    const notes = computed<IV1ReferenceNote[]>(() => reference.value?.notes ?? []);

    // Details card (DS DetailList rows). Amount carries the fee inline in its
    // display string ("$750 + Comisión $17"); term is the amount's caption.
    const detailItems = computed<DetailListItem[]>(() => {
      const r = reference.value;
      if (!r) return [];
      return [
        { label: t("reference-amount-label"), caption: r.term, value: r.amount.display },
        { label: t("method"), value: r.methodDisplay },
        { label: t("beneficiary"), value: r.beneficiary },
      ];
    });

    const instructions = computed<string>(() => {
      const r = reference.value;
      return r && "instructions" in r ? r.instructions ?? "" : "";
    });

    // The Markdown `instructions` string uses `\n` line breaks → one step per line.
    const steps = computed<string[]>(() =>
      instructions.value
        .split("\n")
        .map((line) => line.trim())
        .filter((line) => line.length > 0),
    );

    // DS `DetailList` numbered items. The DS auto-parses `**bold**` in each
    // label (no prop needed), preserving the STP/OXXO emphasis, and renders the
    // dark index badges natively.
    const stepItems = computed<DetailListItem[]>(() =>
      steps.value.map((label) => ({ label })),
    );

    const mapUrl = computed<string>(() => {
      const r = reference.value;
      return r && "mapUrl" in r ? r.mapUrl ?? "" : "";
    });

    const instructionsImage = computed<string>(() => {
      const r = reference.value;
      return r && "instructionsImageUrl" in r ? r.instructionsImageUrl ?? "" : "";
    });

    const onPrimary = (): void => {
      const r = reference.value;
      if (r && r.type === V1PaymentReferenceType.Redirect) {
        window.location.href = r.redirectUrl;
        return;
      }
      router.push(`/v1/device/${route.params.deviceTag}`);
    };

    return {
      t,
      loading,
      error,
      errorInfo,
      reference,
      isRedirect,
      isCodeType,
      codes,
      notes,
      detailItems,
      steps,
      stepItems,
      mapUrl,
      instructionsImage,
      onPrimary,
    };
  },
});
</script>

<style scoped>
.v1-reference {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  min-height: calc(100vh - 32px);
  background-color: var(--pj-color-brand-gray-050, #fafafa);
}

/* Desktop: cards go full-bleed inside the centered 400px column (Figma 3140-2872
   has no horizontal gutter), matching the V1 scaffold's desktop treatment. */
@media (min-width: 768px) {
  .v1-reference {
    padding-left: 0;
    padding-right: 0;
  }
}

.v1-reference__instructions {
  padding: 4px 0;
}

.v1-reference__instructions-image {
  display: block;
  margin: 24px auto 0;
}

.v1-reference__map {
  display: flex;
  flex-direction: column;
}

.v1-reference__map-frame {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
}

.v1-reference__map-frame iframe {
  border: 1px solid var(--pj-color-brand-gray-350, #c6c7bf);
  width: 100%;
  height: 65vw;
  border-radius: var(--pj-radius-xl, 16px);
}

/* "Tiendas cercanas" pill overlaid on the map's top-left corner (Figma §3e). */
.v1-reference__map-pill {
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: var(--pj-radius-full, 1000px);
  background: var(--pj-color-brand-white, #ffffff);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
  color: var(--pj-color-brand-gray-850, #222926);
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  letter-spacing: -0.5px;
}

.v1-reference__map-pill svg {
  color: var(--pj-color-brand-blue-500, #2f4bff);
}

.v1-reference__cta {
  margin-top: auto;
  padding-top: 16px;
  display: flex;
  flex-direction: column;
}

/* Figma redesign (PYMT-2288): primary CTA is dark (#222926), not the legacy
   global green. Scoped so the old-UI `button.primary` is unaffected. */
.v1-reference__cta .primary {
  background: var(--pj-color-brand-gray-850, #222926);
  color: var(--pj-color-brand-white, #ffffff);
}

.v1-reference__cta .primary:hover:not(:disabled) {
  background: var(--pj-color-brand-gray-650, #3f4340);
}
</style>
